import { getPropertyAddress, isValidIban } from "../../utils/verifier";

export const MAX_BULK_ROWS = 500;
export const TEMPLATES = ["electricity", "gas", "associations"];

export function isRonCurrency(currency) {
	return String(currency ?? "").trim().toLowerCase() === "ron";
}

export function parseAmountToCents(amount) {
	const n = Number(String(amount ?? "").replace(",", "."));
	if (!Number.isFinite(n) || n <= 0) return null;
	const cents = Math.round(n * 100);
	if (!Number.isFinite(cents) || cents <= 0) return null;
	return cents;
}

export function formatCents(cents) {
	return (Number(cents || 0) / 100).toFixed(2);
}

export function getOwnerName(property) {
	if (!property?.owner) return "";
	if (typeof property.owner === "string") return property.owner;
	return property.owner.name || "";
}

export function paymentMatchesTemplate(template, paymentType) {
	if (template === "electricity") return paymentType === "electricityUtility";
	if (template === "gas") return paymentType === "gasUtility";
	if (template === "associations") {
		return paymentType !== "electricityUtility" && paymentType !== "gasUtility";
	}
	return false;
}

export function getPaymentDateKey(payment) {
	return payment?.dueDate || payment?.issuedDate || "";
}

export function getEligibilityReason(template, property, payment) {
	if (!isRonCurrency(payment?.currency)) return "Not in RON";
	if (parseAmountToCents(payment?.amount) == null) return "Invalid amount";
	if ((template === "electricity" || template === "gas") && !String(payment?.invoiceNumber ?? "").trim()) {
		return "Missing invoice number";
	}
	if (template === "electricity" && !String(property?.utilities?.electricity?.atmCode ?? "").trim()) {
		return "Missing ATM code";
	}
	if (template === "gas" && !String(property?.utilities?.gas?.paymentCode ?? "").trim()) {
		return "Missing collection code";
	}
	return null;
}

export function getDefaultDetails(template, property, payment) {
	if (template === "electricity") {
		return {
			details1: String(property?.utilities?.electricity?.atmCode ?? "").trim(),
			details2: String(payment?.invoiceNumber ?? "").trim(),
		};
	}
	if (template === "gas") {
		return {
			details1: String(property?.utilities?.gas?.paymentCode ?? "").trim(),
			details2: String(payment?.invoiceNumber ?? "").trim(),
		};
	}
	return {
		details1: getPropertyAddress(property),
		details2: getOwnerName(property),
	};
}

export function getDefaultRecipient(property) {
	const association = property?.association;
	if (association && typeof association === "object" && association.id && isValidIban(association.iban)) {
		return { kind: "association", id: association.id };
	}
	return null;
}

export function flattenTemplatePayments(properties, template) {
	const rows = [];
	for (const property of properties || []) {
		for (const payment of property.payments || []) {
			if (!paymentMatchesTemplate(template, payment.type)) continue;
			const cents = parseAmountToCents(payment.amount);
			const reason = getEligibilityReason(template, property, payment);
			rows.push({
				key: `${property.id}#${payment.id}`,
				propertyId: property.id,
				paymentId: payment.id,
				paymentType: payment.type,
				address: getPropertyAddress(property),
				ownerName: getOwnerName(property),
				dueDate: getPaymentDateKey(payment),
				amount: payment.amount,
				currency: payment.currency,
				invoiceNumber: payment.invoiceNumber || "",
				cents: cents || 0,
				selectable: !reason,
				reason,
				paid: Boolean(payment.paymentDate),
				defaults: getDefaultDetails(template, property, payment),
				defaultRecipient: getDefaultRecipient(property),
			});
		}
	}
	return rows;
}

export function filterPaymentRows(
	rows,
	{
		search = "",
		status = "all",
		dueFrom = "",
		dueTo = "",
		selectableOnly = false,
		hideInBatch = false,
		pairIndex = null,
	} = {}
) {
	const q = String(search).trim().toLowerCase();
	return (rows || []).filter((row) => {
		if (status === "unpaid" && row.paid) return false;
		if (status === "paid" && !row.paid) return false;
		if (dueFrom && row.dueDate && row.dueDate < dueFrom) return false;
		if (dueTo && row.dueDate && row.dueDate > dueTo) return false;
		if (selectableOnly && !row.selectable) return false;
		if (hideInBatch && pairIndex?.has?.(row.key)) return false;
		if (!q) return true;
		return (
			row.address?.toLowerCase().includes(q) ||
			row.ownerName?.toLowerCase().includes(q) ||
			row.invoiceNumber?.toLowerCase().includes(q)
		);
	});
}

export function sortPaymentRows(rows) {
	return [...(rows || [])].sort((a, b) => {
		const ownerCmp = String(a.ownerName || "").localeCompare(String(b.ownerName || ""), undefined, {
			sensitivity: "base",
		});
		if (ownerCmp !== 0) return ownerCmp;
		const addressCmp = String(a.address || "").localeCompare(String(b.address || ""), undefined, {
			sensitivity: "base",
		});
		if (addressCmp !== 0) return addressCmp;
		const aDate = a.dueDate || "";
		const bDate = b.dueDate || "";
		if (!aDate && bDate) return 1;
		if (aDate && !bDate) return -1;
		return String(aDate).localeCompare(String(bDate));
	});
}

export function buildBatchPairIndex(batches) {
	const index = new Map();
	for (const batch of batches || []) {
		const createdAt = batch.createdAt || "";
		for (const pair of batch.pairs || []) {
			const key = `${pair.propertyId}#${pair.paymentId}`;
			const existing = index.get(key);
			if (!existing || String(createdAt).localeCompare(String(existing.createdAt)) > 0) {
				index.set(key, { batchId: batch.batchId, createdAt });
			}
		}
	}
	return index;
}

export function formatBatchBadgeDate(iso) {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	const dd = String(d.getDate()).padStart(2, "0");
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const yyyy = d.getFullYear();
	return `${dd}.${mm}.${yyyy}`;
}

export function providersWithIban(providers) {
	return (providers || []).filter((provider) => String(provider?.iban ?? "").trim());
}

export function recipientsWithValidIban(associations, providers) {
	const items = [];
	for (const association of associations || []) {
		if (association?.id && isValidIban(association.iban)) {
			items.push({ kind: "association", id: association.id, name: association.name || association.id });
		}
	}
	for (const provider of providers || []) {
		if (provider?.id && isValidIban(provider.iban)) {
			items.push({ kind: "provider", id: provider.id, name: provider.name || provider.id });
		}
	}
	return items;
}

export function sumSelectedCents(rows, selectedKeys) {
	const set = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
	let total = 0;
	for (const row of rows || []) {
		if (set.has(row.key) && row.selectable) total += row.cents;
	}
	return total;
}

export function canGenerateReviewRows(template, reviewRows) {
	if (!reviewRows?.length) return false;
	for (const row of reviewRows) {
		if (!String(row.details1 ?? "").trim() || !String(row.details2 ?? "").trim()) return false;
		if (template === "associations" && (!row.recipient?.kind || !row.recipient?.id)) return false;
	}
	return true;
}
